import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Star, Upload, X, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SubmitReview = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (token) {
      fetch(`/api/reviews/verify-token/${token}`)
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            setTokenData(data);
            setName(data.customerName || '');
            setEmail(data.customerEmail || '');
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [token]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      alert('Maximum 5 photos allowed');
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch('/api/uploads/image', { method: 'POST', body: formData });
        const data = await res.json();
        if (data.success) {
          setPhotos(prev => [...prev, data.url]);
        }
      } catch {}
    }
    setUploading(false);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { alert('Please select a star rating'); return; }
    if (!text.trim()) { alert('Please write your review'); return; }

    setSubmitting(true);
    try {
      const res = await fetch('/api/reviews/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: name,
          customerEmail: email,
          orderNumber: tokenData?.orderNumber || '',
          rating, title, text, photos, token
        })
      });
      if (!res.ok) throw new Error();

      if (token) {
        fetch(`/api/reviews/mark-token-used/${token}`, { method: 'POST' }).catch(() => {});
      }
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f7f5f2' }}>
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#FF8C19' }} />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="bg-white rounded-lg shadow-lg p-10 text-center max-w-md mx-4">
        <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>Thank You!</h2>
        <p className="mb-6" style={{ color: '#736c64' }}>
          Your review has been submitted and will appear on our site once approved.
        </p>
        <Button onClick={() => navigate('/reviews')} className="text-white font-semibold" style={{ backgroundColor: '#FF8C19' }}>
          View All Reviews
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
            Share Your Experience
          </h1>
          <p style={{ color: '#736c64' }}>We'd love to hear what you think of our maple syrup!</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
          {/* Name & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Your Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Email *</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com" />
            </div>
          </div>

          {/* Star Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#4a2c1a' }}>Rating *</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className="h-8 w-8"
                    fill={star <= (hoverRating || rating) ? '#FF8C19' : 'none'}
                    style={{ color: star <= (hoverRating || rating) ? '#FF8C19' : '#e5d5c0' }}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 self-center text-sm font-medium" style={{ color: '#FF8C19' }}>
                  {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                </span>
              )}
            </div>
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Review Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sum it up in a few words" />
          </div>

          {/* Review Text */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Your Review *</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              rows={4}
              className="w-full border rounded-lg p-3 text-sm"
              style={{ borderColor: '#e5d5c0' }}
              placeholder="Tell us about your experience with our maple syrup..."
            />
          </div>

          {/* Photo Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#4a2c1a' }}>
              Photos (optional, max 5)
            </label>
            <div className="flex flex-wrap gap-3 mb-3">
              {photos.map((photo, i) => (
                <div key={i} className="relative">
                  <img src={photo} alt="" className="w-20 h-20 object-cover rounded-lg border-2" style={{ borderColor: '#e5d5c0' }} />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors" style={{ borderColor: '#e5d5c0' }}>
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: '#736c64' }} />
                  ) : (
                    <>
                      <Upload className="h-5 w-5" style={{ color: '#736c64' }} />
                      <span className="text-xs mt-1" style={{ color: '#736c64' }}>Add</span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || rating === 0}
            className="w-full text-white font-semibold py-6 text-lg"
            style={{ backgroundColor: '#FF8C19' }}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SubmitReview;
